import React, { useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import Button from '../components/button';
import { AppPage } from '../components/appPage';
import { appService } from '../services/appService';
import { roomStore } from '../stores/roomStore';
import { appLocalStore } from '../stores/appLocalStore';

const FUNNY_MESSAGES = [
  // Fantasy themed
  "Summoning the werewolves...",
  "Consulting the village elders...",
  "Preparing the full moon...",
  "Gathering the pack...",
  "Opening the ancient tome...",
  "Casting moonlight spells...",
  "Awakening the forest spirits...",
  "Brewing the transformation potion...",
  "Sharpening silver arrows...",
  "Lighting mystical torches...",
  "Calling the night creatures...",
  "Reading wolf prophecies...",
  "Enchanting the game board...",
  "Forging silver bullets...",
  "Consulting the oracle...",
  "Preparing the sacred circle...",
  "Invoking ancient curses...",
  "Howling at the moon...",
  "Stirring the cauldron...",
  "Deciphering runes...",
  // App themed
  "Herding digital cats...",
  "Untangling the WebRTC cables...",
  "Teaching servers to howl...",
  "Convincing packets to cooperate...",
  "Negotiating with the internet...",
  "Asking the cloud nicely...",
  "Downloading more RAM...",
  "Bribing the firewall...",
  "Turning it off and on again...",
  "Sprinkling magic bits...",
  "Compiling emotions...",
  "Optimizing the fun algorithm...",
  "Debugging the chaos...",
  "Refreshing the coffee...",
  "Calibrating the randomizer...",
  "Syncing with the matrix...",
  "Buffering the excitement...",
  "Caching the suspense...",
  "Loading the drama...",
  "Initializing the plot twists..."
];

const Lobby: React.FC = observer(() => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get a random message different from the current one
  const getRandomMessage = useCallback((currentMessage: string): string => {
    const availableMessages = FUNNY_MESSAGES.filter(msg => msg !== currentMessage);
    const randomIndex = Math.floor(Math.random() * availableMessages.length);
    return availableMessages[randomIndex];
  }, []);

  // Cleanup function to clear interval timer
  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Effect to manage the creating room message rotation
  useEffect(() => {
    if (roomStore.isCreatingRoom) {
      // Reset to "Creating..." at the start
      roomStore.setCreatingRoomMessage('Creating...');
      
      // Update message every 3 seconds
      intervalRef.current = setInterval(() => {
        // Access the store value directly to avoid stale closure
        const currentMessage = roomStore.creatingRoomMessage;
        const newMessage = getRandomMessage(currentMessage);
        roomStore.setCreatingRoomMessage(newMessage);
      }, 3000);
    } else {
      clearTimers();
    }
  }, [roomStore.isCreatingRoom, getRandomMessage, clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return (
    <AppPage
      className="app--bgBlue"
      content={
        <div className="bg-[var(--bg-primary)] p-10 rounded-2xl shadow-2xl max-w-md w-[90%]">
          <h1 className="text-[var(--text-primary)] text-4xl font-bold m-0 mb-2 text-center">Werewolf</h1>
          <p className="text-[var(--text-secondary)] text-base m-0 mb-8 text-center">Choose an option to begin</p>
          
          <div className="flex flex-col gap-4">
            <Button 
              onClick={() => appService.hostRoom()}
              disabled={roomStore.isCreatingRoom}
              fullWidth
            >
              {roomStore.isCreatingRoom ? roomStore.creatingRoomMessage : 'Host a Game'}
            </Button>
            
            <Button 
              onClick={()=>appLocalStore.navigateToPage("join")}
              fullWidth
            >
              Join a Game
            </Button>
          </div>
        </div>
      }
    />
  );
});

export default Lobby;
